import { useState, useEffect, useRef } from 'react';
import { XMarkIcon, PlusIcon, DocumentDuplicateIcon, FunnelIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const getDisplayName = (name) => {
  if (!name) return '';
  if (typeof name === 'string') return name;
  if (typeof name === 'object') {
    const values = Object.values(name);
    return values.find(v => v) || '';
  }
  return String(name);
};

export default function FilamentPresetSelector({ onSelectPreset, onCreateNew, onCancel }) {
  const [presets, setPresets] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialExtras, setMaterialExtras] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filters, setFilters] = useState({
    material: '',
    materialExtra: '',
    brand: '',
    search: ''
  });

  const selectAllRef = useRef(null);

  useEffect(() => {
    fetchFilterOptions();
    fetchPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPresets();
    setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.material, filters.materialExtra, filters.brand]);

  // Sync the indeterminate state of the select-all checkbox
  useEffect(() => {
    if (!selectAllRef.current) return;
    const allSelected = presets.length > 0 && selectedIds.size === presets.length;
    const someSelected = selectedIds.size > 0 && selectedIds.size < presets.length;
    selectAllRef.current.checked = allSelected;
    selectAllRef.current.indeterminate = someSelected;
  }, [selectedIds, presets]);

  const fetchFilterOptions = async () => {
    try {
      const [materialsRes, extrasRes, brandsRes] = await Promise.all([
        api.get('/auth/materials', { params: { ownership: 'system' } }),
        api.get('/auth/material-extras', { params: { ownership: 'system' } }),
        api.get('/auth/filament-brands', { params: { ownership: 'system' } })
      ]);
      setMaterials(materialsRes.data.data || []);
      setMaterialExtras(extrasRes.data.data || []);
      setBrands(brandsRes.data.data || []);
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  };

  const fetchPresets = async () => {
    setLoading(true);
    try {
      const params = { ownership: 'system' };
      if (filters.material) params.material = filters.material;
      if (filters.materialExtra) params.extra = filters.materialExtra;
      if (filters.brand) params.brand = filters.brand;
      if (filters.search) params.q = filters.search;

      const response = await api.get('/auth/filaments', { params });
      setPresets(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch presets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      fetchPresets();
      setSelectedIds(new Set());
    }
  };

  const handleSearchBlur = () => {
    fetchPresets();
    setSelectedIds(new Set());
  };

  const toggleCard = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allSelected = selectedIds.size === presets.length && presets.length > 0;
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(presets.map((p) => p._id)));
    }
  };

  const handleAddSelected = async () => {
    if (selectedIds.size === 0) return;
    setDuplicating(true);
    setError('');
    try {
      const response = await api.post('/auth/filaments/batch-duplicate', {
        ids: Array.from(selectedIds),
        from: 'system',
      });
      onSelectPreset(response.data.data || { created: [], skipped: [] });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add selected filaments');
    } finally {
      setDuplicating(false);
    }
  };

  const clearFilters = () => {
    setFilters({ material: '', materialExtra: '', brand: '', search: '' });
    setSelectedIds(new Set());
  };

  const hasActiveFilters = filters.material || filters.materialExtra || filters.brand || filters.search;

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Add Filament</h2>
            <p className="text-sm text-slate-500 mt-1">Select one or more presets, or create from scratch</p>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 transition-colors">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 mb-3">
            <FunnelIcon className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Filter presets</span>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-xs text-blue-600 hover:text-blue-800 ml-2">
                Clear all
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="Search by name..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={handleSearchKeyDown}
              onBlur={handleSearchBlur}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <select
              value={filters.material}
              onChange={(e) => setFilters({ ...filters, material: e.target.value })}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">All Materials</option>
              {materials.map((m) => (
                <option key={m._id} value={m._id}>{m.name} ({m.key})</option>
              ))}
            </select>
            <select
              value={filters.materialExtra}
              onChange={(e) => setFilters({ ...filters, materialExtra: e.target.value })}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">All Extras</option>
              {materialExtras.map((me) => (
                <option key={me._id} value={me._id}>{getDisplayName(me.name)} ({me.key})</option>
              ))}
            </select>
            <select
              value={filters.brand}
              onChange={(e) => setFilters({ ...filters, brand: e.target.value })}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">All Brands</option>
              {brands.map((b) => (
                <option key={b._id} value={b._id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Select all row */}
          {!loading && presets.length > 0 && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
              <input
                ref={selectAllRef}
                type="checkbox"
                id="select-all"
                onChange={toggleSelectAll}
                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
              />
              <label htmlFor="select-all" className="text-sm text-slate-600 cursor-pointer select-none">
                Select all {presets.length} result{presets.length !== 1 ? 's' : ''}
              </label>
              {selectedIds.size > 0 && (
                <span className="ml-auto text-sm font-medium text-blue-700">
                  {selectedIds.size} selected
                </span>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-slate-500">Loading presets...</div>
            </div>
          ) : presets.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-slate-400 text-lg mb-2">No presets found</div>
              <p className="text-sm text-slate-500 mb-4">
                {hasActiveFilters
                  ? 'Try adjusting your filters or create a new filament from scratch.'
                  : 'No system presets available. Create a new filament from scratch.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {presets.map((preset) => {
                const isSelected = selectedIds.has(preset._id);
                return (
                  <div
                    key={preset._id}
                    onClick={() => !duplicating && toggleCard(preset._id)}
                    className={`border rounded-lg p-4 cursor-pointer transition-all group relative ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                        : 'border-slate-200 hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    {/* Checkbox top-right */}
                    <div className="absolute top-3 right-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                    </div>

                    <div className="flex items-start gap-3 pr-6">
                      {/* Color swatch */}
                      <div
                        className="w-12 h-12 rounded-lg border border-slate-300 shadow-inner flex-shrink-0"
                        style={{ backgroundColor: preset.color?.hex ? `#${preset.color.hex}` : '#f1f5f9' }}
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-medium truncate ${isSelected ? 'text-blue-700' : 'text-slate-900 group-hover:text-blue-600'}`}>
                          {preset.fullName || preset.name}
                        </h3>
                        <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                          <div>
                            {typeof preset.material === 'object'
                              ? `${preset.material.name} (${preset.material.key})`
                              : preset.material}
                            {preset.materialExtra && (
                              <span className="ml-1 text-slate-400">
                                + {typeof preset.materialExtra === 'object'
                                  ? getDisplayName(preset.materialExtra.name) || preset.materialExtra.key
                                  : preset.materialExtra}
                              </span>
                            )}
                          </div>
                          {preset.color?.name && (
                            <div className="text-slate-400">{preset.color.name}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                      <span className="text-slate-400">
                        {preset.diameter}mm · {preset.weight}g
                      </span>
                      <span className="text-slate-500 font-medium">
                        €{preset.costKg?.toFixed(2)}/kg
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <div className="text-sm text-slate-500">
            {!loading && `${presets.length} preset${presets.length !== 1 ? 's' : ''} available`}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={duplicating}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onCreateNew}
              disabled={duplicating}
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Create from Scratch
            </button>
            <button
              type="button"
              onClick={handleAddSelected}
              disabled={duplicating || selectedIds.size === 0}
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
              {duplicating
                ? 'Adding...'
                : selectedIds.size > 0
                  ? `Add Selected (${selectedIds.size})`
                  : 'Add Selected'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
